import React, { useCallback, useLayoutEffect, useMemo } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import Logo from 'design-system/Logo';
import Text from 'design-system/Text';
import StatusBar from 'design-system/StatusBar';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import IconReceiptSolid from 'design-system/icons/IconReceiptSolid';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import NavigationCloseButton from 'design-system/NavigationCloseButton';
import { AppView, ButtonGroup, SettingsItemNavigate, Spacer, withAnimatedNavigationHeader } from 'design-system';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import { useOrderCache, useStockMeta } from '../hooks';
import Receipt from '../components/Receipt';
import { selectActiveTradingAccountId } from '../reducers/selectors';
import { isWeb } from '../../../constants';
const strings = {
    close: 'Close',
    subtitle: {
        queued: 'Your switch is placed. We will execute it as soon as market opens',
        cancelled: 'Your switch is cancelled',
        failed: 'We were unable to process your switch',
        partially_filled: 'Your switch was partially filled',
    },
};
const SwitchOrderScreen = withAnimatedNavigationHeader(({ route, navigation, scrollHandler }) => {
    const { assetId, switchAssetId, status: statusFromProps, switchOrderId, id } = route.params;
    const fromStock = useStockMeta(assetId);
    const toStock = useStockMeta(switchAssetId);
    const tradingAccountId = useAppSelector(selectActiveTradingAccountId);
    const order = useOrderCache(id, tradingAccountId);
    const status = order?.status || statusFromProps;
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const onDone = useCallback(() => {
        navigation.goBack();
    }, [navigation]);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerLeft: () => <NavigationCloseButton inWebHeader onPress={onDone}/>,
        });
    }, [onDone]);
    const title = useMemo(() => `Switch from ${fromStock?.name || assetId} to ${toStock?.name || switchAssetId}`, [assetId, switchAssetId, fromStock?.name, toStock?.name]);
    const rows = useMemo(() => {
        const out = {};
        if (fromStock?.name) {
            out.from = (<View style={styles.row}>
            <Spacer w={16}/>
            <View style={styles.flex}>
              <Text Text-14 style={styles.stockName}>
                {fromStock.name}
              </Text>
            </View>
            <Spacer w={16}/>
            <Logo R24 uri={fromStock.iconUrl}/>
          </View>);
        }
        if (toStock?.name) {
            out.to = (<View style={styles.row}>
            <Spacer w={16}/>
            <View style={styles.flex}>
              <Text Text-14 style={styles.stockName}>
                {toStock.name}
              </Text>
            </View>
            <Spacer w={16}/>
            <Logo R24 uri={toStock.iconUrl}/>
          </View>);
        }
        return out;
    }, [fromStock, styles.flex, styles.row, toStock, styles.stockName]);
    const seeDetails = useCallback(() => {
        navigation.navigate('InvestSwitchOrderDetails', { id, switchOrderId, assetId, switchAssetId });
    }, [assetId, id, switchAssetId, switchOrderId]);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle, styles.container);
    return (<AppView>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyle}>
          <Receipt isSwitch rows={rows} title={title} alwaysShowStatus status={status} logoUrl={fromStock?.iconUrl}/>
          {status !== 'pending' && status !== 'cancelled' && (<>
              <Spacer h={16}/>
              <TouchableBounceCard onDark noMargin onPress={seeDetails}>
                <View pointerEvents="none">
                  <SettingsItemNavigate icon={<IconReceiptSolid />} title="See details"/>
                </View>
              </TouchableBounceCard>
            </>)}
        </Animated.ScrollView>
        <ButtonGroup type="floating">
          <Button key="close" brand title={strings.close} onPress={onDone}/>
        </ButtonGroup>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export const styleSet = createStyleSheets(() => ({
    container: {
        paddingHorizontal: rem(16),
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
    },
    flex: {
        flex: 1,
    },
    stockName: {
        textAlign: 'right',
    },
}));
export default SwitchOrderScreen;
